import { defer } from 'epubjs/src/utils/core';
import { EVENTS } from 'epubjs/src/utils/constants';
import ContinuousViewManager from 'epubjs/src/managers/continuous';
import DefaultViewManager from 'epubjs/src/managers/default';

/**
 * A view only has a usable section while the book is alive.
 * Book.destroy() destroys the spine first, which nulls every section's prev/next,
 * so queued work (e.g. a scroll-triggered check() still sitting in the rAF queue)
 * can run against dead sections after teardown.
 *
 * @param {Object} view - The view to check
 * @returns {boolean} True if the view's section can still be traversed
 */
const hasLiveSection = (view) =>
  !!view && !!view.section && typeof view.section.prev === 'function' && typeof view.section.next === 'function';

/**
 * HeadingBasedContinuousViewManager
 *
 * A custom continuous view manager that extends epub.js's ContinuousViewManager
 * to provide enhanced scrolling performance and virtual scrolling capabilities.
 *
 * Key Features:
 * - Virtual scrolling: Only renders views (iframes) that are near the current viewport
 * - Dynamic loading: Adds content sections ahead/behind as user scrolls
 * - Memory efficient: Uses hybrid limits (max 65 iframes OR 5x viewport height total DOM)
 * - Smooth navigation: Handles prepending content without scroll jumps
 * - Teleport system: Multi-phase state machine for jump navigation (TOC, bookmarks)
 * - View protection: Keeps specific views loaded for audio playback during scrolling
 */
class HeadingBasedContinuousViewManager extends ContinuousViewManager {
  CONFIG = {
    MAX_AWAKE_VIEWS: 65, // Hard limit on active iframes
    DYNAMIC_PADDING_VH: 5, // Viewport height multiplier for teleport padding
    APPEND_BUFFER_PX: 1500, // How far ahead to load
    PREPEND_BUFFER_PX: 800, // How far behind to load
    TELEPORT_BUILD_DELAY_MS: 2000, // Time to wait before building backward buffer
    SHIELD_DEBOUNCE_MS: 150, // Time to ignore phantom native scroll events
    SETTLE_DELAY_MS: 100, // Time to let DOM paint before final snap
    INITIAL_SNAP_DELAY_MS: 50, // Initial target snap delay after display
    DOM_PAINT_DELAY_MS: 15, // Delay between batch view loading
    DISPLAY_DEBOUNCE_MS: 1500, // Prevent duplicate display() calls
  };

  // TELEPORT STATE MACHINE
  // When a user jumps to a distant chapter via CFI (e.g., clicking TOC), we enter
  // a strict multi-phase timeline to prevent scroll jumps and race conditions:
  //
  // PHASE 1: NAVIGATION LOCKDOWN (isNavigating = true)
  //   - Protects DOM during initial snap
  //   - Mutes onScroll() events
  //   - Duration: Until initial fill() completes
  //
  // PHASE 2: FORWARD-ONLY TELEPORT (isTeleporting = true)
  //   - Forces forward-only appending to build scroll container
  //   - Blocks backward prepending to prevent anchor shifts
  //   - Adds dynamic padding to prevent premature scroll limits
  //   - Duration: 2000ms after initial display
  //
  // PHASE 3: EVENT SUPPRESSION (isSuppressingEvents = true)
  //   - Mutes Vue location update events
  //   - Prevents jumps during backward buffer building
  //   - Returns cached location to external systems
  //   - Duration: Spans PHASE 2 + settling time
  //
  // PHASE 4: TARGET LOCK (centerViewIndex = N)
  //   - Pins Garbage Collector to orbit around target chapter
  //   - Ensures target never unloads during buffer building
  //   - Duration: Entire teleport sequence until final snap
  //
  // PHASE 5: ANCHOR SHIELD (isAdjustingAnchor = true)
  //   - Mutes phantom native scroll events after programmatic scrollTop changes
  //   - Prevents prevScrollTop corruption
  //   - Duration: 150ms after each scroll adjustment
  //
  // Timeline:
  // display() → [PHASE 1] → fill() → [PHASES 2,3,4] → check() builds buffers
  // → [PHASE 5] final snap → release all locks

  // Teleport Timeline Control Flags
  isNavigating = false;
  isTeleporting = false;
  isSuppressingEvents = false;
  isAdjustingAnchor = false;
  centerViewIndex = undefined;

  // Loading State Flags
  isFilling = false;
  isInitialFillComplete = false;
  hasPendingCheck = false;

  // Other State
  scrollDirection = 0;
  protectedViewIndex = null;

  /**
   * Protects a specific chapter's iframe from being unloaded by the virtual scroller
   * This ensures seamless audio playback even when the user scrolls to distant chapters
   * @param {number|null} index - Spine index to protect, or null to clear protection
   */
  protectView(index) {
    this.protectedViewIndex = index;
  }

  /**
   * Evaluates if a specific chapter index falls within the current dynamic active zone.
   * Used by the audio player to determine if it should auto-scroll/follow along,
   * or let the user explore freely without yanking the scrollbar.
   */
  isViewInActiveZone(targetIndex) {
    const views = this.views.all();
    if (!views.length) return false;

    // Find exactly where the user is looking right now
    let currentIndex = 0;
    const currentLoc = this.currentLocation();

    if (currentLoc && currentLoc.length > 0) {
      currentIndex = currentLoc[0].index;
    } else {
      // Use Absolute Scroll Math instead of Viewport Math
      const vph = this._viewportHeight || window.innerHeight;
      const absoluteCenter = this._getScrollTop() + vph / 2;

      let closestView = views[0];
      let minDistance = Infinity;

      views.forEach((v) => {
        if (v.element) {
          const top = v.offset()?.top ?? v.element.offsetTop;
          const h = v.height() || v.element.offsetHeight;

          const viewCenter = top + h / 2;
          const dist = Math.abs(viewCenter - absoluteCenter);

          if (dist < minDistance) {
            minDistance = dist;
            closestView = v;
          }
        }
      });
      if (closestView) currentIndex = closestView.index;
    }

    // If they are physically looking right at it, it's definitely active
    if (currentIndex === targetIndex) return true;

    // Replicate the Dynamic Hybrid Math to simulate the "awake" set
    const viewportHeight = this._viewportHeight || window.innerHeight;
    const DYNAMIC_PIXEL_CAP = viewportHeight * 5;
    const MAX_AWAKE_VIEWS = this.CONFIG.MAX_AWAKE_VIEWS;

    const viewsWithMetrics = views.map((view) => {
      return {
        index: view.index,
        distance: Math.abs(view.index - currentIndex),
        height: view.element ? view.element.offsetHeight || 0 : 0,
      };
    });

    // Radiate outward from the user's current position
    viewsWithMetrics.sort((a, b) => a.distance - b.distance);

    let awakeViewsCount = 0;
    let awakePixelsHeight = 0;

    for (const item of viewsWithMetrics) {
      const isCoreNeighbor = awakeViewsCount < 3;
      const isUnderPixelCap = awakePixelsHeight < DYNAMIC_PIXEL_CAP;
      const isUnderViewCap = awakeViewsCount < MAX_AWAKE_VIEWS;

      if (item.distance === 0 || isCoreNeighbor || (isUnderPixelCap && isUnderViewCap)) {
        // If the target view is allowed to wake up under these rules, it's in the active zone!
        if (item.index === targetIndex) {
          return true;
        }
        awakeViewsCount++;
        awakePixelsHeight += item.height;
      }
    }

    // If the loop finished and we never hit the target, it's outside the buffer
    return false;
  }

  /** Helper: Gets the current scroll position safely */
  _getScrollTop() {
    return this.settings.fullsize ? window.scrollY : this.container.scrollTop || 0;
  }

  /** Helper: Sets the scroll position safely */
  _setScrollTop(targetPixel) {
    if (this.settings.fullsize) {
      window.scrollTo(0, targetPixel);
      this.prevScrollTop = window.scrollY;
    } else {
      this.container.scrollTop = targetPixel;
      this.prevScrollTop = this.container.scrollTop;
    }
  }

  /**
   * Initializes the view manager and sets up scroll handling
   * @param {HTMLElement} element - Container element for the book content
   * @param {Object} size - Dimensions for the container
   */
  render(element, size) {
    super.render(element, size);

    // Disable overflow anchor to prevent browser from auto-adjusting scroll when content is prepended
    Object.assign(this.container.style, { overflowAnchor: 'none' });

    let scroller = !this.settings.fullsize ? this.container : window;

    // Re-attach scroll listener with passive flag for better performance
    if (this._onScroll) {
      scroller.removeEventListener('scroll', this._onScroll);
      scroller.addEventListener('scroll', this._onScroll, { passive: true });
    }
  }

  /**
   * Displays a specific section and fills surrounding content
   * @param {Object} section - The book section to display
   * @param {string} target - Optional target location within the section
   * @returns {Promise} Resolves when section is displayed and filled
   */
  display(section, target) {
    const now = Date.now();

    if (this.isSuppressingEvents) return Promise.resolve();
    if (
      now - (this.lastDisplayEndTime || 0) < this.CONFIG.DISPLAY_DEBOUNCE_MS &&
      this.activeDisplayTarget === target &&
      target !== undefined
    ) {
      return Promise.resolve();
    }

    this.activeDisplayTarget = target;

    // TARGET LOCK: Tell the Garbage Collector to NEVER unload this section!
    this.centerViewIndex = section.index;

    this.isNavigating = true;
    this.isSuppressingEvents = true;
    this.isTeleporting = true;

    const viewportHeight = this.settings.fullsize
      ? window.innerHeight
      : this.container.getBoundingClientRect().height || window.innerHeight;
    // Cache before padding is applied — container.clientHeight will include
    // paddingBottom once set, making it useless as a real viewport measurement.
    this._viewportHeight = viewportHeight;
    const dynamicPadding = viewportHeight * 5;

    const scrollerEl = this.settings.fullsize ? document.body : this.container;
    scrollerEl.style.paddingBottom = `${dynamicPadding}px`;

    return DefaultViewManager.prototype.display
      .call(this, section, target)
      .then(() => {
        // eslint-disable-next-line consistent-return
        return new Promise((resolve) => {
          if (!target) return resolve();
          setTimeout(() => {
            const view = this.views.find(section);
            if (view) {
              const localLoc = view.locationOf(target);
              if (localLoc) {
                const globalOffset = view.offset();
                const targetScrollTop = globalOffset.top + localLoc.top;

                this._setScrollTop(targetScrollTop);
              }
            }
            resolve();
          }, this.CONFIG.INITIAL_SNAP_DELAY_MS);
        });
      })
      .then(() => {
        this.isNavigating = false;
        return this.fill(undefined, section);
      })
      .finally(() => {
        this.lastDisplayEndTime = Date.now();
        this.isSuppressingEvents = false;

        // Wake up the Audio Player. Because we removed the unlock from onScroll,
        // the Target Lock safely survives this call!
        this.onScroll();

        setTimeout(() => {
          this.isTeleporting = false;
          this.isSuppressingEvents = true;

          scrollerEl.style.paddingBottom = '';

          this.check(0, 0, section).finally(() => {
            setTimeout(() => {
              this.isSuppressingEvents = false;

              const targetView = this.views.find(section);
              if (targetView) {
                const loc = targetView.locationOf(target);
                if (loc) {
                  const newGlobalTop = targetView.offset().top + loc.top;

                  // RAISE THE SHIELD FOR THE FORCE-SNAP
                  this.isAdjustingAnchor = true;

                  // THE FORCE-SNAP
                  this._setScrollTop(newGlobalTop);

                  // Drop the shield after SHIELD_DEBOUNCE_MS, after the native scroll events clear out
                  clearTimeout(this._anchorTimeout);
                  this._anchorTimeout = setTimeout(() => {
                    this.isAdjustingAnchor = false;
                    // Emit SCROLLED directly so reportLocation() fires the correct `relocated` event
                    // for the force-snap position. Avoids calling onScroll() which would trigger
                    // _scrolled() → check() → update() and prune the pre-built buffer.
                    this.emit(EVENTS.MANAGERS.SCROLLED, {
                      top: this._getScrollTop(),
                      left: 0,
                    });
                  }, this.CONFIG.SHIELD_DEBOUNCE_MS);
                }
              }

              // NOW we drop the Target Lock
              this.centerViewIndex = undefined;
            }, this.CONFIG.SETTLE_DELAY_MS);
          });
        }, this.CONFIG.TELEPORT_BUILD_DELAY_MS);
      });
  }

  /**
   * Recursively fills the viewport with content sections
   * @param {defer} _full - Optional deferred promise for tracking completion
   * @param {Object} section - The section to start filling from
   * @returns {Promise} Resolves when filling is complete
   */
  fill(_full, section) {
    const full = _full || new defer();

    // Queue a check operation to see if more content is needed
    this.q
      .enqueue(() => this.check(0, 0, section, false))
      .then((result) => {
        // If check returns true, more content is needed - recursively fill
        if (result) {
          this.fill(full);
        } else {
          // Filling complete
          full.resolve();
        }
      });
    return full.promise;
  }

  /**
   * Virtual scrolling update - activates/deactivates views based on proximity to current position
   * This is the core of the virtualization strategy, using hybrid limits to control active iframes
   * @param {number} _offset - Optional offset parameter (not currently used)
   * @returns {Promise} Resolves when all view updates are complete
   */
  update(_offset) {
    const views = this.views.all();
    const promises = [];

    // INDEX CALCULATION WITH TARGET LOCK
    let currentIndex = 0;

    if (this.centerViewIndex !== undefined) {
      // Target Lock Active! Orbit exactly around the chapter we just navigated to.
      currentIndex = this.centerViewIndex;
    } else {
      // Normal behavior: ask epub.js where the user is looking
      const currentLoc = this.currentLocation();
      if (currentLoc && currentLoc.length > 0) {
        currentIndex = currentLoc[0].index;
      } else {
        // FIX: Use Absolute Scroll Math instead of Viewport Math
        const absoluteCenter = this._getScrollTop() + (this._viewportHeight || window.innerHeight) / 2;

        let closestView = views[0];
        let minDistance = Infinity;

        views.forEach((v) => {
          if (v.element) {
            const top = v.offset()?.top ?? v.element.offsetTop;
            const h = v.height() || v.element.offsetHeight;

            const viewCenter = top + h / 2;
            const dist = Math.abs(viewCenter - absoluteCenter);

            if (dist < minDistance) {
              minDistance = dist;
              closestView = v;
            }
          }
        });
        if (closestView) currentIndex = closestView.index;
      }
    }

    // Use the cached pre-padding viewport height so the pixel cap is stable
    // even when container.clientHeight is inflated by dynamic padding.
    const viewportHeight = this._viewportHeight || window.innerHeight;
    const DYNAMIC_PIXEL_CAP = viewportHeight * 5;
    const MAX_AWAKE_VIEWS = this.CONFIG.MAX_AWAKE_VIEWS;

    const viewsWithMetrics = views.map((view) => {
      return {
        view,
        distance: Math.abs(view.index - currentIndex),
        height: view.element ? view.element.offsetHeight || 0 : 0,
      };
    });

    // Sort radiating outwardly from the current chapter
    viewsWithMetrics.sort((a, b) => a.distance - b.distance);

    // THE VIRTUAL SCROLL LOOP
    // Iterate through all views and activate/deactivate based on distance from the current view
    let awakeViewsCount = 0;
    let awakePixelsHeight = 0;
    const viewsToWake = new Set();

    // --- THE EVALUATION LOOP ---
    for (const item of viewsWithMetrics) {
      // THE BYPASS RULE:
      // Even if Chapter 1 is 20,000px tall, we MUST guarantee that at least 3 chapters
      // (Current, Prev, Next) are allowed to wake up so you never hit a white boundary wall.
      const isCoreNeighbor = awakeViewsCount < 3;

      const isUnderPixelCap = awakePixelsHeight < DYNAMIC_PIXEL_CAP;
      const isUnderViewCap = awakeViewsCount < MAX_AWAKE_VIEWS;

      // Wake it up if it's the chapter we are looking at, a core neighbor, OR fits in the dynamic budget
      if (item.distance === 0 || isCoreNeighbor || (isUnderPixelCap && isUnderViewCap)) {
        viewsToWake.add(item.view);
        awakeViewsCount++;
        awakePixelsHeight += item.height;
      }
    }

    // EXECUTE WAKE / SLEEP STATE DECISIONS
    for (let i = 0; i < views.length; i++) {
      let view = views[i];

      // Protection check: Is this the chapter currently playing audio?
      // Protected views bypass virtualization and stay loaded regardless of scroll position
      const isProtected = this.protectedViewIndex === view.index;

      if (viewsToWake.has(view) || isProtected) {
        // WAKE UP / ACTIVE ZONE (in viewport OR protected)
        if (!view.displayed) {
          // Display and show the view if not already displayed
          promises.push(
            view.display(this.request).then(
              (v) => v.show(),
              (_err) => view.hide(),
            ),
          );
        } else {
          // Just show it if already displayed
          view.show();
        }
      } else {
        // SLEEP ZONE (Virtualize)
        // Replaces the iframe with a fixed-height skeleton div to save memory
        if (view.displayed && typeof view.unload === 'function') {
          view.unload();
        }
      }
    }

    // Wait for all view updates to complete
    const updating = new defer();
    if (promises.length) {
      return Promise.all(promises).catch((err) => updating.reject(err));
    }
    updating.resolve();
    return updating.promise;
  }

  /**
   * Overrides the default location calculator to protect against race conditions.
   * If the DOM is actively shifting (isSuppressingEvents), return the last known good location.
   */
  currentLocation() {
    if (this.isSuppressingEvents && this._lastKnownLocation) {
      return this._lastKnownLocation;
    }

    const loc = super.currentLocation();

    if (!this.isSuppressingEvents) {
      this._lastKnownLocation = loc;
    }

    return loc;
  }

  /**
   * Handles scroll events and tracks scroll direction
   * Called whenever the user scrolls the content
   */
  onScroll() {
    if (this.isNavigating || this.isSuppressingEvents || this.isAdjustingAnchor) {
      return;
    }

    const currentScrollTop = this.container.scrollTop;
    this.scrollDirection = currentScrollTop - (this.prevScrollTop ?? currentScrollTop);
    this.prevScrollTop = currentScrollTop;

    super.onScroll();
  }

  /**
   * Checks if more content needs to be loaded and triggers prepend/append operations
   * This is the core loading logic that maintains the buffer zones around the viewport
   *
   * @param {number} _offsetLeft - Horizontal offset (for horizontal scrolling)
   * @param {number} _offsetTop - Vertical offset (for vertical scrolling)
   * @param {Object} section - The section being checked
   * @param {boolean} recursive - Whether this is a recursive call during filling
   * @returns {Promise<boolean>} Resolves to true if more content was added, false otherwise
   */
  check(_offsetLeft, _offsetTop, section, recursive = false) {
    const checking = new defer();

    if (this.isFilling && !recursive) {
      if (!this.hasPendingCheck) {
        this.hasPendingCheck = true;
        this.q.enqueue(() => {
          this.hasPendingCheck = false;
          return this.check(_offsetLeft, _offsetTop, section, recursive);
        });
      }
      checking.resolve(false);
      return checking.promise;
    }

    const newViews = [];
    const horizontal = this.settings.axis === 'horizontal';
    let delta = this.settings.offset || 0;

    if (_offsetLeft && horizontal) delta = _offsetLeft;
    if (_offsetTop && !horizontal) delta = _offsetTop;

    const bounds = this._bounds;

    let offset = this._getScrollTop();
    if (offset < 0) offset = 0;

    const visibleLength = horizontal ? Math.floor(bounds.width) : bounds.height;
    const contentLength = horizontal ? this.container.scrollWidth : this.container.scrollHeight;

    const viewCount = this.views ? this.views.length : 0;
    if (!this.isInitialFillComplete && viewCount >= 3) {
      this.isInitialFillComplete = true;
    }

    const batchSize = this.isInitialFillComplete ? 3 : 5;

    const prepend = () => {
      const first = this.views.first();
      let prev = hasLiveSection(first) ? first.section.prev() : null;

      if (prev && !this.views.find(prev)) {
        for (let i = 0; i < batchSize; i++) {
          newViews.push(this.prepend(prev));
          prev = prev.prev();
          if (!prev || this.views.find(prev)) break;
        }
      }
    };

    const append = () => {
      const last = this.views.last();
      let next = hasLiveSection(last) ? last.section.next() : null;

      if (next) {
        for (let i = 0; i < batchSize; i++) {
          newViews.push(this.append(next));
          next = next.next();
          if (!next) break;
        }
      }
    };

    const end = offset + visibleLength + delta;
    const start = offset - delta;
    const isInitialLoad = contentLength === 0 || contentLength < visibleLength;

    const firstView = this.views.first();
    const hasPrev = hasLiveSection(firstView) ? !!firstView.section.prev() : false;

    const lastView = this.views.last();
    const hasNext = hasLiveSection(lastView) ? !!lastView.section.next() : false;

    const appendBuffer = Math.min(visibleLength * 2, this.CONFIG.APPEND_BUFFER_PX);

    // We removed the messy forwardViewsCount logic.
    // Just use epub.js's native math. Because of the dynamic padding in display(),
    // 'end' is massive, which naturally forces the forward rocket to build!
    if ((isInitialLoad || end >= contentLength - appendBuffer) && hasNext) {
      append();
    }

    const prependBuffer = Math.min(visibleLength * 2, this.CONFIG.PREPEND_BUFFER_PX);

    if (this.isTeleporting) {
      // Teleport active: Skip backward prepending to protect anchor
    } else if (start < prependBuffer && hasPrev && !this.isNavigating) {
      prepend();
    }

    const promises = newViews.map((view) => view.display(this.request));

    if (newViews.length) {
      this.isFilling = true;
      return (
        Promise.all(promises)
          .then(() => new Promise((resolve) => setTimeout(resolve, this.CONFIG.DOM_PAINT_DELAY_MS)))
          // Unrestricted recursion restored! No more manual kill switches.
          .then(() => this.check(0, 0, undefined, true))
          .then(() => {
            this.isFilling = false;
            return this.update(delta);
          })
          .catch((err) => {
            this.isFilling = false;
            return err;
          })
      );
    }

    this.q.enqueue(() => this.update());
    checking.resolve(false);
    return checking.promise;
  }

  /**
   * Prepends a section to the beginning of the view list
   * Handles scroll position adjustment to prevent content from jumping
   *
   * @param {Object} section - The section to prepend
   * @returns {View} The newly created view
   */
  prepend(section) {
    const view = this.createView(section);

    view.on('resized', (bounds) => {
      if (view.expanded) return;

      // RAISE PHANTOM SCROLL SHIELD
      // We are about to move the scrollbar programmatically.
      // Tell onScroll() to completely ignore the resulting native scroll events
      this.isAdjustingAnchor = true;

      const oldScroll = this._getScrollTop();
      this._setScrollTop(oldScroll + bounds.height);

      // Drop the shield after SHIELD_DEBOUNCE_MS, after the async native scroll events have fired
      clearTimeout(this._anchorTimeout);
      this._anchorTimeout = setTimeout(() => {
        this.isAdjustingAnchor = false;
      }, this.CONFIG.SHIELD_DEBOUNCE_MS);

      view.expanded = true;
    });

    view.on('axis', (axis) => this.updateAxis(axis));
    view.on('writingMode', (mode) => this.updateWritingMode(mode));
    this.views.insert(view, 0);
    view.onDisplayed = this.afterDisplayed.bind(this);
    return view;
  }

  /**
   * Clear method - intentionally empty
   * Override parent's clear to prevent unwanted clearing behavior
   */
  clear() {}
}

export default HeadingBasedContinuousViewManager;
